import { createPrepareReadinessApi } from "@wakeloop/api-client";
import {
  PREPARE_SESSION_STREAM_PROTOCOL_VERSION,
  prepareSessionResponseSchema,
  prepareSessionStreamServerFrameSchema,
} from "@wakeloop/schemas";
import type {
  PrepareLocalAgentCatalog,
  PrepareLocalReadinessSnapshot,
  PrepareSessionSnapshot,
  PrepareSessionStreamServerFrame,
  PrepareStepId,
} from "@wakeloop/schemas/types";
import { getSetupFlowCopy } from "@wakeloop/start";
import { sleep } from "@wakeloop/utils";
import {
  type AuthSessionStateResult,
  getAuthSessionState,
} from "../config/auth-session";
import { readDaemonServiceContract } from "../config/daemon-service-contract";
import {
  appendLocalAgentsGuideUrl,
  buildLocalAgentsGuideUrl,
  buildNoLocalAgentsNoticeLines,
  confirmContinueWithoutLocalAgents,
} from "../guides/local-agents-guide-url";
import {
  assertSelectedAgentControllerReadinessReady,
  assertSelectedAgentControllerReportsAccepted,
  resolveSelectedAgentControllerReadiness,
  type SelectedAgentControllerReadinessDecision,
  type SelectedAgentControllerReportResult,
  submitSelectedAgentControllerReports,
} from "../local-service/reporting/selected-agent-controller-reporting";
import { buildLocalAgentCatalogFromReadiness } from "../local-service/state/current-device-identity";
import { notifyDaemonRouteCatalogChanged } from "../local-service/state/route-catalog-sync-notifier";
import { formatWakeLoopCliCommand } from "../runtime/cli-entry-command";
import { getCliVersionInfo } from "../runtime/cli-version";
import type { createPresenter } from "../runtime/presenter/index";
import type { RuntimeContext } from "../runtime/types";
import { createCliApiClientFoundation } from "../space/api-client-foundation";
import { fetchSpaceRequestWithTimeout } from "../space/fetch-with-timeout";
import { runStartLocalAgentsEnable } from "../start-local/agents-local";
import { collectStartLocalReadiness } from "../start-local/readiness";
import type { StartReadinessSnapshot } from "../start-local/readiness-types";
import { runDaemonServiceParticipationGate } from "../system/local-service-participation-gate";
import { connectWebSocket } from "../transport/ws";
import { resolveControllingTerminalSession } from "../ui/controlling-terminal";
import { renderInfoCard } from "../ui/info-card";
import {
  promptSearchMultiselect,
  promptSearchSelect,
} from "../ui/search-select";
import {
  createSetupProgressReporter,
  type SetupProgressStepHandle,
} from "../ui/setup-progress";
import { runAgentsRepair } from "./agents";
import {
  collectPrepareStepDiagnostics,
  type PrepareServiceJobVerdict,
  resolvePrepareServiceJobVerdict,
} from "./prepare-step-diagnostics";
import { syncLocalSetupCurrentDeviceTargetFromServiceContract } from "./setup-current-device";

const PREPARE_TOKEN_HEADER = "x-wakeloop-prepare-token";
const PREPARE_SESSION_MAX_RECONNECT_ATTEMPTS = 3;
const PREPARE_SESSION_RECONNECT_DELAY_MS = 1000;
const REPORT_RUNTIME_CONNECTION_MAX_ATTEMPTS = 12;
const REPORT_RUNTIME_CONNECTION_RETRY_DELAY_MS = 1000;
const WAITING_PROGRESS_DELAY_MS = 2000;

const TERMINAL_PREPARE_STEP_ORDER: readonly PrepareStepId[] = [
  "prepare_selected_agents",
  "refresh_service",
  "report_runtime",
  "verify_setup",
];

class PrepareSessionBlockedByCoreError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PrepareSessionBlockedByCoreError";
  }
}

type PrepareSessionExecutorInput = {
  gatewayUrl: string;
  startSession: string;
  localAgentSelection?: PrepareSessionLocalAgentSelection;
  runtime: RuntimeContext;
  presenter: ReturnType<typeof createPresenter>;
  interactive: boolean;
  view?: string;
};

export type PrepareSessionExecutorResult =
  | {
      completionFacts?: PrepareSessionExecutorCompletionFacts;
      snapshot: PrepareSessionSnapshot;
      status: "completed";
    }
  | {
      reason: "terminal_replaced";
      status: "not_completed";
    };

export type PrepareSessionExecutorCompletionFacts = {
  wakeLoopServiceRuntimeIdentity: {
    deviceId: string;
    runtimeLane: string;
  } | null;
  runtimeReports: readonly SelectedAgentControllerReportResult[];
  selectedLocalAgentIds: readonly string[];
};

export type PrepareSessionLocalAgentSelection =
  | {
      mode: "all";
    }
  | {
      mode: "none";
    }
  | {
      localAgentIds: string[];
      mode: "specific";
    };

export async function runPrepareSessionExecutor(
  input: PrepareSessionExecutorInput
): Promise<PrepareSessionExecutorResult> {
  let initial = await resolvePrepareSessionToken({
    gatewayUrl: input.gatewayUrl,
    opaqueToken: input.startSession,
  });
  const alreadyCompleted = finishIfPrepareSessionAlreadyCompleted({
    input,
    snapshot: initial,
  });
  if (alreadyCompleted) {
    return alreadyCompleted;
  }

  let reconnectAttempts = 0;
  while (true) {
    try {
      return await runPrepareSessionExecutorOnce({ input, initial });
    } catch (error) {
      if (error instanceof PrepareSessionTerminalReplacedError) {
        emitHumanStatus(input, "Terminal replaced", [
          "A newer Terminal command is running this setup.",
          "You can close this window.",
        ]);
        return {
          reason: "terminal_replaced",
          status: "not_completed",
        };
      }
      if (
        error instanceof PrepareSessionConnectionInterruptedError &&
        reconnectAttempts < PREPARE_SESSION_MAX_RECONNECT_ATTEMPTS
      ) {
        reconnectAttempts += 1;
        emitHumanStatus(input, "Reconnecting", [
          "WakeLoop Web connection was interrupted.",
          "Reconnecting to the setup session...",
        ]);
        await sleep(PREPARE_SESSION_RECONNECT_DELAY_MS);
        initial = await resolvePrepareSessionToken({
          gatewayUrl: input.gatewayUrl,
          opaqueToken: input.startSession,
        });
        const completedAfterReconnect = finishIfPrepareSessionAlreadyCompleted({
          input,
          snapshot: initial,
        });
        if (completedAfterReconnect) {
          return completedAfterReconnect;
        }
        continue;
      }
      throw error;
    }
  }
}

function finishIfPrepareSessionAlreadyCompleted(input: {
  input: PrepareSessionExecutorInput;
  snapshot: PrepareSessionSnapshot;
}): PrepareSessionExecutorResult | null {
  if (input.snapshot.lifecycleStatus !== "completed") {
    return null;
  }
  if (input.input.localAgentSelection) {
    assertCompletedPrepareSessionMatchesTerminalSelection({
      selection: input.input.localAgentSelection,
      snapshot: input.snapshot,
    });
  }
  emitHumanSection(input.input, "WakeLoop setup");
  emitPrepareProgressLine(input.input, {
    state: "success",
    text: "Setup is already complete. You can return to WakeLoop Web.",
  });
  return {
    snapshot: input.snapshot,
    status: "completed",
  };
}

async function runPrepareSessionExecutorOnce(input: {
  initial: PrepareSessionSnapshot;
  input: PrepareSessionExecutorInput;
}): Promise<PrepareSessionExecutorResult> {
  // WAKELOOP_SETUP_TO_WAKE_PATH: HIGHEST_FACT setup-session
  // The local terminal is now attached to the Web-issued setup handoff. Phase 2
  // should route real setup state through this path, not parallel setup flows.
  emitHumanSection(input.input, "WakeLoop setup");
  const connectionProgress = startPrepareProgressStep(input.input, {
    code: "prepare.session.progress.connect_terminal",
    text: "Connecting Terminal to WakeLoop Web...",
  });

  let stream: PrepareSessionStream | null = null;
  try {
    stream = await connectPrepareSessionStream({
      gatewayUrl: input.input.gatewayUrl,
      opaqueToken: input.input.startSession,
      prepareSessionId: input.initial.prepareSessionId,
    });
    const connected = await waitForSnapshotWithProgress({
      input: input.input,
      stream,
      progress: connectionProgress,
      predicate: (snapshot) =>
        snapshot.terminalConnection.status === "connected",
      waitingMessage:
        "Still connecting. Keep this Terminal open; WakeLoop Web will update automatically",
    });
    await enforcePrepareSessionTerminalAuth({
      input: input.input,
      stream,
      snapshot: connected,
    });
    connectionProgress.complete("Terminal connected. Keep this window open.");
    const withLocalAgents = await reportLocalAgentCatalogForSelection({
      input: input.input,
      stream,
      snapshot: connected,
    });
    const selected = await resolveAgentSelection({
      input: input.input,
      stream,
      snapshot: withLocalAgents,
    });
    if (selected.selectedLocalAgentIds.length === 0) {
      return {
        snapshot: selected,
        status: "completed",
      };
    }
    const completed = await executeSelectedAgentPreparation({
      input: input.input,
      stream,
      snapshot: selected,
    });
    return {
      ...(completed.completionFacts
        ? { completionFacts: completed.completionFacts }
        : {}),
      snapshot: completed.snapshot,
      status: "completed",
    };
  } catch (error) {
    connectionProgress.cancel();
    throw error;
  } finally {
    stream?.close();
  }
}

export async function resolveAgentSelection(input: {
  input: PrepareSessionExecutorInput;
  stream: PrepareSessionStream;
  snapshot: PrepareSessionSnapshot;
}): Promise<PrepareSessionSnapshot> {
  if (input.input.localAgentSelection) {
    const guideUrl = buildLocalAgentsGuideUrl(input.input.gatewayUrl);
    const localAgentIds = resolveTerminalSelectedLocalAgentIds({
      guideUrl,
      selection: input.input.localAgentSelection,
      snapshot: input.snapshot,
    });
    if (
      hasAgentSelectionBeenSaved(input.snapshot) &&
      haveSameLocalAgentIds(input.snapshot.selectedLocalAgentIds, localAgentIds)
    ) {
      return input.snapshot;
    }
    return await writeTerminalAgentSelection({
      ...(localAgentIds.length === 0 &&
      input.input.localAgentSelection.mode !== "none"
        ? { emptySelectionGuideUrl: guideUrl }
        : {}),
      input: input.input,
      localAgentIds,
      stream: input.stream,
      snapshot: input.snapshot,
    });
  }
  if (hasAgentSelectionBeenSaved(input.snapshot)) {
    return input.snapshot;
  }
  const localAgentIds = await resolveTerminalInteractiveLocalAgentSelection({
    input: input.input,
    snapshot: input.snapshot,
  });
  return await writeTerminalAgentSelection({
    input: input.input,
    localAgentIds,
    stream: input.stream,
    snapshot: input.snapshot,
  });
}

const PROMPT_LOCAL_AGENT_SELECTION_MESSAGE =
  "Choose the local agents to connect to WakeLoop on this computer";

// No --local-agent(s) flag: choose the local agents right here in the Terminal.
// Under the `curl | sh` setup bootstrap the CLI runs with stdin redirected to
// /dev/null, so resolveControllingTerminalSession opens /dev/tty to drive the
// prompt. When no controlling terminal exists (CI, fully headless) WakeLoop connects
// all detected agents instead of bouncing the user to WakeLoop Web.
async function resolveTerminalInteractiveLocalAgentSelection(input: {
  input: PrepareSessionExecutorInput;
  snapshot: PrepareSessionSnapshot;
}): Promise<string[]> {
  const selectableEntries = resolveSelectableLocalAgentCatalogEntries(
    input.snapshot
  );
  if (selectableEntries.length === 0) {
    await confirmZeroLocalAgentPrepareSessionIfNeeded({
      input: input.input,
    });
    return [];
  }

  const allSelectableIds = selectableEntries.map((entry) => entry.agentId);
  const terminal =
    input.input.runtime.resolvedView === "human"
      ? resolveControllingTerminalSession()
      : null;
  if (!terminal) {
    emitPrepareProgressLine(input.input, {
      state: "pending",
      text: `No interactive terminal is available. WakeLoop will connect all ${
        allSelectableIds.length
      } detected local agent${allSelectableIds.length === 1 ? "" : "s"}.`,
    });
    emitPrepareProgressDetail(
      input.input,
      "Use --local-agents all|none or --local-agent <id> to choose without a prompt."
    );
    return allSelectableIds;
  }

  try {
    const result = await promptSearchMultiselect<string>(
      {
        message: PROMPT_LOCAL_AGENT_SELECTION_MESSAGE,
        items: selectableEntries.map((entry) => ({
          value: entry.agentId,
          label: entry.displayName,
        })),
        initialSelectedValues: allSelectableIds,
        required: false,
      },
      terminal.streams
    );
    if (result.status !== "submitted") {
      emitHumanStatus(input.input, "Setup cancelled", [
        "Local agent selection was cancelled.",
        "Run the setup command from WakeLoop Web again when you're ready.",
      ]);
      throw new Error(
        "WakeLoop setup was cancelled before choosing local agents."
      );
    }
    return result.value;
  } finally {
    terminal.close();
  }
}

async function confirmZeroLocalAgentPrepareSessionIfNeeded(input: {
  input: PrepareSessionExecutorInput;
}): Promise<void> {
  const guideUrl = buildLocalAgentsGuideUrl(input.input.gatewayUrl);
  const terminal =
    input.input.runtime.resolvedView === "human"
      ? resolveControllingTerminalSession()
      : null;
  if (!terminal) {
    emitPrepareNoLocalAgentsNotice({
      guideUrl,
      input: input.input,
    });
    return;
  }

  try {
    const decision = await confirmContinueWithoutLocalAgents({
      guideUrl,
      streams: terminal.streams,
    });
    if (decision === "continue") {
      return;
    }
    emitHumanStatus(input.input, "Setup cancelled", [
      "Install an agent CLI, then re-run the setup command from WakeLoop Web.",
      `Guide: ${guideUrl}`,
    ]);
    throw new Error(
      "WakeLoop setup was cancelled before choosing local agents."
    );
  } finally {
    terminal.close();
  }
}

function emitPrepareNoLocalAgentsNotice(input: {
  guideUrl: string;
  input: PrepareSessionExecutorInput;
}): void {
  const [summary, ...details] = buildNoLocalAgentsNoticeLines({
    guideUrl: input.guideUrl,
  });
  emitPrepareProgressLine(input.input, {
    state: "success",
    text: summary
      ? `${summary} Setup will continue without local agents.`
      : "Setup will continue without local agents.",
  });
  for (const detail of details) {
    emitPrepareProgressDetail(input.input, detail);
  }
}

function resolveSelectableLocalAgentCatalogEntries(
  snapshot: PrepareSessionSnapshot
): PrepareLocalAgentCatalog["entries"] {
  return (snapshot.localAgentCatalog?.entries ?? []).filter(
    (entry) => entry.selectable
  );
}

export function resolveTerminalSelectedLocalAgentIds(input: {
  guideUrl?: string;
  selection: PrepareSessionLocalAgentSelection;
  snapshot: PrepareSessionSnapshot;
}): string[] {
  if (input.selection.mode === "none") {
    return [];
  }
  const catalog = input.snapshot.localAgentCatalog;
  if (!catalog) {
    throw new Error("WakeLoop could not load the local agent catalog.");
  }

  const selectableById = new Map(
    catalog.entries
      .filter((entry) => entry.selectable)
      .map((entry) => [entry.agentId, entry])
  );

  if (input.selection.mode === "all") {
    return catalog.entries
      .filter((entry) => entry.selectable)
      .map((entry) => entry.agentId);
  }

  const selectedIds = [...new Set(input.selection.localAgentIds)];
  const missingIds = selectedIds.filter(
    (agentId) => !selectableById.has(agentId)
  );
  if (missingIds.length > 0) {
    const selectableIds = [...selectableById.keys()];
    throw new Error(
      [
        `WakeLoop cannot select local agent ${missingIds.join(", ")} for setup.`,
        selectableIds.length > 0
          ? `Selectable local agents: ${selectableIds.join(", ")}.`
          : formatNoSelectableLocalAgentsFound(input.guideUrl),
      ].join(" ")
    );
  }
  return selectedIds;
}

function formatNoSelectableLocalAgentsFound(guideUrl?: string): string {
  const message = "No selectable local agents were found on this computer.";
  return guideUrl ? appendLocalAgentsGuideUrl({ guideUrl, message }) : message;
}

export function assertCompletedPrepareSessionMatchesTerminalSelection(input: {
  selection: PrepareSessionLocalAgentSelection;
  snapshot: PrepareSessionSnapshot;
}): void {
  const localAgentIds = resolveTerminalSelectedLocalAgentIds(input);
  if (
    haveSameLocalAgentIds(input.snapshot.selectedLocalAgentIds, localAgentIds)
  ) {
    return;
  }
  throw new Error(
    [
      "This WakeLoop setup already completed with a different local-agent selection.",
      "Generate a fresh WakeLoop Agent Interface prompt, then run the new setup command.",
    ].join(" ")
  );
}

async function writeTerminalAgentSelection(input: {
  emptySelectionGuideUrl?: string;
  input: PrepareSessionExecutorInput;
  localAgentIds: readonly string[];
  stream: PrepareSessionStream;
  snapshot: PrepareSessionSnapshot;
}): Promise<PrepareSessionSnapshot> {
  const beforeWriteSnapshot =
    input.stream.getLatestSnapshot() ?? input.snapshot;
  const beforeWriteRevision = beforeWriteSnapshot.sessionRevision;
  const connectionEpoch = requireConnectionEpoch(beforeWriteSnapshot);
  input.stream.writeTerminalAgentSelection({
    connectionEpoch,
    expectedRevision: beforeWriteRevision,
    localAgentIds: [...input.localAgentIds],
  });
  const selectedSnapshot = await input.stream.waitForSnapshot((snapshot) => {
    return (
      snapshot.sessionRevision > beforeWriteRevision &&
      hasAgentSelectionBeenSaved(snapshot)
    );
  });
  emitPrepareProgressLine(input.input, {
    state: "success",
    text:
      input.localAgentIds.length === 0
        ? formatSelectedNoLocalAgents(input.emptySelectionGuideUrl)
        : `Selected: ${formatSelectedLocalAgentNames({
            localAgentIds: input.localAgentIds,
            snapshot: selectedSnapshot,
          })}`,
  });
  return selectedSnapshot;
}

function formatSelectedNoLocalAgents(guideUrl?: string): string {
  const message = "Selected: no local agents";
  return guideUrl ? appendLocalAgentsGuideUrl({ guideUrl, message }) : message;
}

export async function reportLocalAgentCatalogForSelection(input: {
  input: PrepareSessionExecutorInput;
  stream: PrepareSessionStream;
  snapshot: PrepareSessionSnapshot;
}): Promise<PrepareSessionSnapshot> {
  const connectionEpoch = requireConnectionEpoch(input.snapshot);
  emitHumanSection(input.input, "Local agents");
  const progress = startPrepareProgressStep(input.input, {
    code: "prepare.session.progress.local_agents",
    text: "Checking local agents...",
  });
  const readiness = await collectStartLocalReadinessForSetupCatalog(
    input.input,
    progress
  );
  const beforeWriteRevision =
    input.stream.getLatestSnapshot()?.sessionRevision ??
    input.snapshot.sessionRevision;
  input.stream.writeTerminalLocalAgentCatalog({
    connectionEpoch,
    localAgentCatalog: buildLocalAgentCatalogFromReadiness(readiness),
  });
  const reportedSnapshot = await input.stream.waitForSnapshot((snapshot) => {
    return (
      snapshot.sessionRevision > beforeWriteRevision &&
      snapshot.localAgentCatalog !== null
    );
  });
  progress.complete("Local agents checked.");
  return reportedSnapshot;
}

async function collectStartLocalReadinessForSetupCatalog(
  input: PrepareSessionExecutorInput,
  progress: SetupProgressStepHandle
): Promise<StartReadinessSnapshot> {
  const initialReadiness = await collectStartLocalReadinessForSetup(input);
  const repairableAgentIds =
    resolveRepairableSetupLocalAgentIds(initialReadiness);
  if (repairableAgentIds.length === 0) {
    return initialReadiness;
  }

  progress.detail({
    code: "prepare.session.progress.local_agents.repair",
    text:
      repairableAgentIds.length === 1
        ? `Repairing ${formatLocalAgentDisplayName(
            repairableAgentIds.join("")
          )} before setup reports it...`
        : `Repairing ${repairableAgentIds.length} local agents before setup reports them...`,
  });
  for (const agentId of repairableAgentIds) {
    await repairLocalAgentForSetup({ agentId, input, progress });
  }

  const repairedReadiness = await collectStartLocalReadinessForSetup(input);
  const remainingRepairableAgentIds =
    resolveRepairableSetupLocalAgentIds(repairedReadiness);
  if (remainingRepairableAgentIds.length > 0) {
    throw new Error(
      formatSetupLocalAgentRepairStillRequired(remainingRepairableAgentIds)
    );
  }
  return repairedReadiness;
}

async function collectStartLocalReadinessForSetup(
  input: PrepareSessionExecutorInput
): Promise<StartReadinessSnapshot> {
  return await collectStartLocalReadiness({
    auth: {
      status: "valid",
      email: null,
      authBaseUrl: null,
      gatewayUrl: input.gatewayUrl,
    },
  });
}

function resolveRepairableSetupLocalAgentIds(
  readiness: StartReadinessSnapshot
): string[] {
  return [
    ...new Set(
      readiness.agents.candidates
        .filter((candidate) => {
          return (
            candidate.detected &&
            candidate.enabled &&
            candidate.launchStatus === "needs_repair"
          );
        })
        .map((candidate) => candidate.agentId)
    ),
  ];
}

function formatSetupLocalAgentRepairStillRequired(
  agentIds: readonly string[]
): string {
  const formattedAgentIds = agentIds.map(formatLocalAgentDisplayName);
  const repairCommand =
    agentIds.length === 1
      ? `wakeloop agents repair --agent ${agentIds.join("")}`
      : `wakeloop agents repair --agent ${agentIds.join(" ")}`;
  return [
    agentIds.length === 1
      ? `${formattedAgentIds.join("")} still needs local setup repair.`
      : `${formattedAgentIds.join(", ")} still need local setup repair.`,
    `Run \`${formatWakeLoopCliCommand(repairCommand)}\`, then copy a fresh setup command from WakeLoop Web and run setup again.`,
  ].join(" ");
}

function hasAgentSelectionBeenSaved(snapshot: PrepareSessionSnapshot): boolean {
  return snapshot.steps.some(
    (step) => step.id === "choose_local_agents" && step.state === "complete"
  );
}

function haveSameLocalAgentIds(
  left: readonly string[],
  right: readonly string[]
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  const rightSet = new Set(right);
  return left.every((id) => rightSet.has(id));
}

async function executeSelectedAgentPreparation(input: {
  input: PrepareSessionExecutorInput;
  stream: PrepareSessionStream;
  snapshot: PrepareSessionSnapshot;
}): Promise<{
  completionFacts?: PrepareSessionExecutorCompletionFacts;
  snapshot: PrepareSessionSnapshot;
}> {
  let snapshot = input.snapshot;
  let reportRuntimeResult: TerminalStepRunResult | null = null;
  emitHumanSection(input.input, "Setup progress");
  while (snapshot.lifecycleStatus === "active") {
    const stepId = resolveNextTerminalPrepareStep(snapshot);
    if (stepId === null) {
      return { snapshot };
    }
    const connectionEpoch = requireConnectionEpoch(snapshot);
    const terminalStep = buildTerminalStep({
      wakeLoopServiceRuntimeIdentity:
        resolveRuntimeIdentityFromPrepareSessionSnapshot(snapshot),
      humanProfileId: snapshot.humanProfileId,
      input: input.input,
      localAgentIds: snapshot.selectedLocalAgentIds,
      ownerUserId: snapshot.ownerUserId,
      stepId,
    });
    snapshot = await runTerminalStep({
      input: input.input,
      stream: input.stream,
      connectionEpoch,
      stepId,
      snapshot,
      ...terminalStep,
      onRunResult: (result) => {
        if (stepId === "report_runtime" && result?.runtimeReports) {
          reportRuntimeResult = result;
        }
      },
    });
  }
  return {
    ...(reportRuntimeResult
      ? {
          completionFacts: buildPrepareSessionExecutorCompletionFacts({
            reportRuntimeResult,
            selectedLocalAgentIds: snapshot.selectedLocalAgentIds,
          }),
        }
      : {}),
    snapshot,
  };
}

export async function runTerminalStep(input: {
  input: PrepareSessionExecutorInput;
  stream: PrepareSessionStream;
  connectionEpoch: number;
  stepId: PrepareStepId;
  snapshot: PrepareSessionSnapshot;
  message: string;
  run: (
    progress: SetupProgressStepHandle
  ) => Promise<TerminalStepRunResult | undefined>;
  onRunResult?: (result: TerminalStepRunResult | undefined) => void;
  collectDiagnostics?: typeof collectPrepareStepDiagnostics;
  resolveServiceJobVerdict?: typeof resolvePrepareServiceJobVerdict;
}): Promise<PrepareSessionSnapshot> {
  const progress = startPrepareProgressStep(input.input, {
    code: `prepare.session.progress.${input.stepId}`,
    text: input.message,
  });
  const stepStartRevision = input.snapshot.sessionRevision;
  input.stream.writeTerminalStep({
    connectionEpoch: input.connectionEpoch,
    expectedRevision: stepStartRevision,
    stepId: input.stepId,
    status: "started",
  });
  let startedSnapshot: PrepareSessionSnapshot | null = null;
  try {
    startedSnapshot = await input.stream.waitForSnapshot((snapshot) => {
      const step = findStep(snapshot, input.stepId);
      return (
        snapshot.sessionRevision > stepStartRevision && step?.state === "active"
      );
    });
    const wakeLoopServiceRuntimeIdentity = await input.run(progress);
    input.onRunResult?.(wakeLoopServiceRuntimeIdentity);
    const stepCompleteRevision = startedSnapshot.sessionRevision;
    input.stream.writeTerminalStep({
      connectionEpoch: input.connectionEpoch,
      expectedRevision: stepCompleteRevision,
      ...(wakeLoopServiceRuntimeIdentity ?? {}),
      stepId: input.stepId,
      status: "completed",
    });
    const resolvedSnapshot = await input.stream.waitForSnapshot((snapshot) => {
      const step = findStep(snapshot, input.stepId);
      return (
        snapshot.sessionRevision > stepCompleteRevision &&
        (step?.state === "complete" ||
          step?.state === "blocked" ||
          snapshot.phase === "blocked" ||
          snapshot.lifecycleStatus !== "active")
      );
    });
    assertTerminalStepCompletedOrThrow({
      input: input.input,
      snapshot: resolvedSnapshot,
      stepId: input.stepId,
    });
    progress.complete(formatTerminalStepCompleteMessage(input.stepId));
    return resolvedSnapshot;
  } catch (error) {
    if (error instanceof PrepareSessionConnectionInterruptedError) {
      progress.cancel();
      throw error;
    }
    if (error instanceof PrepareSessionBlockedByCoreError) {
      progress.cancel();
      throw error;
    }
    if (error instanceof PrepareSessionRevisionConflictError) {
      const message =
        "This setup changed while Terminal was working. Copy a fresh setup command from WakeLoop Web and run setup again to continue from the latest step.";
      progress.cancel();
      emitHumanStatus(input.input, "Setup changed", [message]);
      throw new Error(message);
    }
    if (startedSnapshot === null) {
      progress.cancel();
      throw error;
    }
    const rawMessage =
      error instanceof Error && error.message.trim().length > 0
        ? error.message.trim()
        : "Setup could not continue.";
    const serviceJobVerdict = await resolveRefreshServiceJobVerdict({
      stepId: input.stepId,
      resolveServiceJobVerdict: input.resolveServiceJobVerdict,
    });
    const message = formatTerminalStepFailureMessage({
      stepId: input.stepId,
      rawMessage,
      serviceJobVerdict,
    });
    const reasonCode = resolveTerminalStepFailureReasonCode({
      rawMessage,
      stepId: input.stepId,
      serviceJobVerdict,
    });
    const diagnostics = await (
      input.collectDiagnostics ?? collectPrepareStepDiagnostics
    )({
      stepId: input.stepId,
      supportCode: reasonCode,
    });
    input.stream.writeTerminalStep({
      connectionEpoch: input.connectionEpoch,
      expectedRevision: startedSnapshot.sessionRevision,
      stepId: input.stepId,
      status: "failed",
      reasonCode,
      message,
      ...(diagnostics === null ? {} : { diagnostics }),
    });
    if (diagnostics !== null) {
      // Bounded settle window so the diagnostics-carrying write (or its
      // stripped resend on cores that predate the field) can land before the
      // stream closes with the thrown failure.
      await waitForTerminalStepWriteToSettle({
        revisionFloor: startedSnapshot.sessionRevision,
        stream: input.stream,
      });
    }
    progress.fail(message);
    emitHumanStatus(input.input, "Setup blocked", [
      message,
      "After fixing this step, copy a fresh setup command from WakeLoop Web and run setup again.",
      ...(diagnostics === null
        ? []
        : [
            `A diagnostic summary was shared with WakeLoop to help fix this (support code: ${reasonCode}).`,
          ]),
    ]);
    const userFacingError = new Error(message);
    (userFacingError as Error & { cause?: unknown }).cause = error;
    throw userFacingError;
  }
}

function assertTerminalStepCompletedOrThrow(input: {
  input: PrepareSessionExecutorInput;
  snapshot: PrepareSessionSnapshot;
  stepId: PrepareStepId;
}): void {
  const step = findStep(input.snapshot, input.stepId);
  if (step?.state === "complete" && input.snapshot.phase !== "blocked") {
    return;
  }

  const blocker =
    input.snapshot.blockers.find(
      (candidate) => candidate.stepId === input.stepId
    ) ??
    input.snapshot.blockers[0] ??
    null;
  const message =
    blocker?.message ??
    "WakeLoop setup is blocked. Return to WakeLoop Web for details.";
  emitHumanStatus(input.input, "Setup blocked", [
    message,
    ...(blocker?.reasonCode ? [`Support code: ${blocker.reasonCode}`] : []),
  ]);
  throw new PrepareSessionBlockedByCoreError(message);
}

export function resolveNextTerminalPrepareStep(
  snapshot: PrepareSessionSnapshot
): PrepareStepId | null {
  if (snapshot.selectedLocalAgentIds.length === 0) {
    return null;
  }
  return (
    TERMINAL_PREPARE_STEP_ORDER.find((stepId) => {
      const step = findStep(snapshot, stepId);
      return step?.state !== "complete";
    }) ?? null
  );
}

export function buildTerminalStep(input: {
  wakeLoopServiceRuntimeIdentity?: TerminalRuntimeIdentity | null;
  humanProfileId?: string | null;
  input: PrepareSessionExecutorInput;
  localAgentIds: readonly string[];
  ownerUserId?: string | null;
  stepId: PrepareStepId;
}): {
  message: string;
  run: (
    progress?: SetupProgressStepHandle
  ) => Promise<TerminalStepRunResult | undefined>;
} {
  switch (input.stepId) {
    case "prepare_selected_agents":
      return {
        message: "Connecting selected local agents...",
        run: async (progress) => {
          const enableResult = await prepareSelectedLocalAgentsForSetup({
            input: input.input,
            localAgentIds: input.localAgentIds,
            progress,
          });
          assertSelectedLocalAgentsEnabledForPrepare(enableResult);
          return undefined;
        },
      };
    case "refresh_service":
      return {
        message: getSetupFlowCopy("setup").cliProgressStartingService,
        run: async (progress) => {
          const gate = await runDaemonServiceParticipationGate({
            intent: "start",
            presenter: input.input.presenter,
            resolvedView: input.input.runtime.resolvedView,
            allowPrompt: input.input.interactive,
            autoRepair: true,
            gatewayUrl: input.input.gatewayUrl,
            ...(input.humanProfileId
              ? { wakeLoopProfileId: input.humanProfileId }
              : {}),
            ...(input.ownerUserId
              ? { expectedOwnerUserId: input.ownerUserId }
              : {}),
            forcePrompt: true,
            ...(progress ? { serviceProgress: progress } : {}),
            suppressRefreshHumanResultCard: true,
            suppressStartSpinner: true,
            view: input.input.view,
          });
          if (gate.status !== "aligned") {
            throw new Error(
              gate.errorMessage ?? "WakeLoop Service is not connected yet."
            );
          }
          progress?.detail({
            code: "prepare.session.progress.refresh_service.identity",
            text: "Saving this computer's WakeLoop Service identity...",
          });
          const wakeLoopServiceRuntimeIdentity =
            await syncLocalSetupCurrentDeviceTargetFromServiceContract({
              gatewayUrl: input.input.gatewayUrl,
            });
          progress?.detail({
            code: "prepare.session.progress.refresh_service.state",
            text: "Checking local setup state...",
          });
          const readiness = await collectStartLocalReadiness({
            auth: {
              status: "valid",
              email: null,
              authBaseUrl: null,
              gatewayUrl: input.input.gatewayUrl,
            },
          });
          return {
            ...wakeLoopServiceRuntimeIdentity,
            localReadinessSnapshot:
              buildPrepareLocalReadinessSnapshot(readiness),
          };
        },
      };
    case "report_runtime":
      return {
        message: "Reporting selected local agents to WakeLoop...",
        run: async () => {
          return await connectSelectedLocalAgentsToWakeLoop({
            ...(input.wakeLoopServiceRuntimeIdentity
              ? {
                  wakeLoopServiceRuntimeIdentity:
                    input.wakeLoopServiceRuntimeIdentity,
                }
              : {}),
            gatewayUrl: input.input.gatewayUrl,
            localAgentIds: input.localAgentIds,
            syncRouteCatalog: true,
          });
        },
      };
    case "verify_setup":
      return {
        message: "Checking that selected local agents are connected...",
        run: async () => {
          return await verifySelectedLocalAgentsConnectedToWakeLoop({
            gatewayUrl: input.input.gatewayUrl,
            localAgentIds: input.localAgentIds,
          });
        },
      };
    default:
      throw new Error(`Terminal cannot run prepare step: ${input.stepId}`);
  }
}

function formatTerminalStepCompleteMessage(stepId: PrepareStepId): string {
  switch (stepId) {
    case "prepare_selected_agents":
      return "Connected selected local agents.";
    case "refresh_service":
      return "WakeLoop Service is running.";
    case "report_runtime":
      return "Reported selected local agents to WakeLoop.";
    case "verify_setup":
      return "Verified selected local agents.";
    default:
      return "Step complete.";
  }
}

type StartLocalAgentsEnableResult = Awaited<
  ReturnType<typeof runStartLocalAgentsEnable>
>;
type StartLocalAgentsEnableError =
  StartLocalAgentsEnableResult["summary"]["errors"][number];
type StartLocalAgentsRepairResult = Awaited<ReturnType<typeof runAgentsRepair>>;
type StartLocalAgentsRepairError =
  StartLocalAgentsRepairResult["summary"]["errors"][number];
type SelectedLocalAgentInstallChoice = NonNullable<
  StartLocalAgentsEnableError["installChoices"]
>[number];
type SelectedLocalAgentInstallChoiceError = {
  agentId: string;
  installChoices?: SelectedLocalAgentInstallChoice[];
  nextSteps?: string[];
  reason: string;
};

async function prepareSelectedLocalAgentsForSetup(input: {
  input: PrepareSessionExecutorInput;
  localAgentIds: readonly string[];
  progress?: SetupProgressStepHandle;
}): Promise<StartLocalAgentsEnableResult> {
  const initialResult = await enableSelectedLocalAgentsForSetup(input);
  if (initialResult.status !== "completed") {
    return initialResult;
  }

  const recoverableErrors = initialResult.summary.errors.filter((error) => {
    return (
      isSelectedLocalAgentNeedsRepairError(error) ||
      error.code === "controller.launch.choice_required"
    );
  });
  if (recoverableErrors.length === 0) {
    return initialResult;
  }

  for (const error of recoverableErrors) {
    await recoverSelectedLocalAgentForSetup({
      error,
      input: input.input,
      progress: input.progress,
    });
  }

  return await enableSelectedLocalAgentsForSetup(input);
}

async function enableSelectedLocalAgentsForSetup(input: {
  input: PrepareSessionExecutorInput;
  installId?: string;
  localAgentIds: readonly string[];
}): Promise<StartLocalAgentsEnableResult> {
  return await runStartLocalAgentsEnable({
    agent: [...input.localAgentIds],
    ...(input.installId ? { install: input.installId } : {}),
    interactive: input.input.interactive,
    presentation: "handoff",
    view: input.input.view,
  });
}

async function recoverSelectedLocalAgentForSetup(input: {
  error: StartLocalAgentsEnableError;
  input: PrepareSessionExecutorInput;
  progress?: SetupProgressStepHandle;
}): Promise<void> {
  if (isSelectedLocalAgentNeedsRepairError(input.error)) {
    await repairSelectedLocalAgentForSetup(input);
    return;
  }

  if (input.error.code === "controller.launch.choice_required") {
    const installId = await resolveSelectedLocalAgentInstallChoice({
      command: "enable",
      error: input.error,
      input: input.input,
      progress: input.progress,
    });
    const enableResult = await enableSelectedLocalAgentsForSetup({
      input: input.input,
      installId,
      localAgentIds: [input.error.agentId],
    });
    assertSelectedLocalAgentRecoveryCompleted({
      errorFormatter: formatSelectedLocalAgentEnableFailure,
      result: enableResult,
    });
  }
}

async function repairSelectedLocalAgentForSetup(input: {
  error: StartLocalAgentsEnableError;
  input: PrepareSessionExecutorInput;
  progress?: SetupProgressStepHandle;
}): Promise<void> {
  await repairLocalAgentForSetup({
    agentId: input.error.agentId,
    input: input.input,
    progress: input.progress,
  });
}

async function repairLocalAgentForSetup(input: {
  agentId: string;
  input: PrepareSessionExecutorInput;
  progress?: SetupProgressStepHandle;
}): Promise<void> {
  const repairResult = await runSelectedLocalAgentRepairForSetup({
    agentId: input.agentId,
    input: input.input,
  });
  const blockingError = resolveRepairBlockingError(repairResult);
  if (!blockingError) {
    if (input.progress) {
      input.progress.detail({
        code: "prepare.session.progress.local_agents.repaired",
        text: `${formatLocalAgentDisplayName(input.agentId)} local setup repaired; continuing setup...`,
      });
    } else {
      emitPrepareProgressLine(input.input, {
        state: "success",
        text: `${formatLocalAgentDisplayName(input.agentId)} local setup repaired.`,
      });
    }
    return;
  }

  if (blockingError.code === "controller.launch.choice_required") {
    const installId = await resolveSelectedLocalAgentInstallChoice({
      command: "repair",
      error: blockingError,
      input: input.input,
      progress: input.progress,
    });
    const selectedRepairResult = await runSelectedLocalAgentRepairForSetup({
      agentId: input.agentId,
      input: input.input,
      installId,
    });
    const selectedBlockingError =
      resolveRepairBlockingError(selectedRepairResult);
    if (!selectedBlockingError) {
      return;
    }
    throw new Error(
      formatSelectedLocalAgentRepairFailure(selectedBlockingError)
    );
  }

  throw new Error(formatSelectedLocalAgentRepairFailure(blockingError));
}

async function runSelectedLocalAgentRepairForSetup(input: {
  agentId: string;
  input: PrepareSessionExecutorInput;
  installId?: string;
}): Promise<StartLocalAgentsRepairResult> {
  return await runAgentsRepair({
    agent: [input.agentId],
    ...(input.installId ? { install: input.installId } : {}),
    interactive: input.input.interactive,
    presentation: "handoff",
    view: input.input.view,
  });
}

function resolveRepairBlockingError(
  result: StartLocalAgentsRepairResult
): StartLocalAgentsRepairError | null {
  if (result.status !== "completed") {
    return {
      agentId: "selected-agent",
      code: "controller.launch.needs_repair",
      reason: "agent repair did not complete",
    };
  }
  return result.summary.errors[0] ?? null;
}

function assertSelectedLocalAgentRecoveryCompleted(input: {
  errorFormatter: (error: StartLocalAgentsEnableError) => string;
  result: StartLocalAgentsEnableResult;
}): void {
  if (input.result.status !== "completed") {
    throw new Error(
      "WakeLoop setup cannot continue until the selected local agent is enabled."
    );
  }
  const blockingError = input.result.summary.errors[0] ?? null;
  if (blockingError) {
    throw new Error(input.errorFormatter(blockingError));
  }
}

async function resolveSelectedLocalAgentInstallChoice(input: {
  command: "enable" | "repair";
  error: SelectedLocalAgentInstallChoiceError;
  input: PrepareSessionExecutorInput;
  progress?: SetupProgressStepHandle;
}): Promise<string> {
  const installChoices = input.error.installChoices ?? [];
  if (installChoices.length === 0) {
    throw new Error(formatSelectedLocalAgentInstallChoiceFailure(input));
  }

  if (installChoices.length === 1) {
    const installChoice = installChoices[0];
    if (!installChoice) {
      throw new Error(formatSelectedLocalAgentInstallChoiceFailure(input));
    }
    const message = `Using ${installChoice.label} for ${formatLocalAgentDisplayName(
      input.error.agentId
    )}.`;
    if (input.progress) {
      input.progress.detail({
        code: "prepare.session.progress.local_agents.install_choice",
        text: message,
      });
    } else {
      emitPrepareProgressLine(input.input, {
        state: "success",
        text: message,
      });
    }
    return installChoice.installId;
  }

  const terminal =
    input.input.runtime.resolvedView === "human"
      ? resolveControllingTerminalSession()
      : null;
  if (!terminal) {
    throw new Error(formatSelectedLocalAgentInstallChoiceFailure(input));
  }

  try {
    const result = await promptSearchSelect(
      {
        message: `Choose the ${formatLocalAgentDisplayName(
          input.error.agentId
        )} install WakeLoop should use`,
        items: installChoices.map((installChoice) => ({
          value: installChoice.installId,
          label: installChoice.label,
          detail: installChoice.detail,
          searchText: [
            installChoice.label,
            installChoice.detail,
            installChoice.commandPath,
            installChoice.installId,
          ].join(" "),
        })),
      },
      terminal.streams
    );
    if (result.status === "submitted") {
      return result.value;
    }
    throw new Error(
      "WakeLoop setup was cancelled before choosing a local agent install."
    );
  } finally {
    terminal.close();
  }
}

function isSelectedLocalAgentNeedsRepairError(
  error: StartLocalAgentsEnableError
): boolean {
  return (
    error.code === "controller.launch.needs_repair" ||
    (error.code === "agents.invalid_agent_id" &&
      normalizeOptionalId(error.reason) === "needs repair")
  );
}

function assertSelectedLocalAgentsEnabledForPrepare(
  result: StartLocalAgentsEnableResult
): void {
  if (result.status !== "completed") {
    throw new Error(
      "WakeLoop setup cannot continue until the selected local agents are enabled. Choose or enable them in Terminal, then run WakeLoop setup again."
    );
  }

  const blockingError = result.summary.errors[0] ?? null;
  if (blockingError) {
    throw new Error(formatSelectedLocalAgentEnableFailure(blockingError));
  }
}

function formatSelectedLocalAgentEnableFailure(
  error: StartLocalAgentsEnableError
): string {
  if (error.code === "controller.launch.choice_required") {
    const commands = resolveSelectedLocalAgentEnableNextSteps(error);
    const reason =
      normalizeOptionalId(error.reason) ||
      `WakeLoop found more than one ${error.agentId} install on this computer.`;
    if (commands.length > 0) {
      return `${reason} Run one of these commands, then return to WakeLoop Web setup: ${formatCommandChoiceList(commands)}.`;
    }
    return `${reason} Run \`${formatWakeLoopCliCommand(
      `wakeloop agents enable --agent ${error.agentId}`
    )}\` in an interactive Terminal and choose one install. If this Terminal cannot prompt, run \`${formatWakeLoopCliCommand(
      `wakeloop agents enable --agent ${error.agentId} --install <install-id>`
    )}\` with one of the install IDs shown by that enable command, then return to WakeLoop Web setup.`;
  }

  if (error.code === "controller.launch.needs_repair") {
    return `WakeLoop could not connect ${error.agentId} on this computer. Run \`${formatWakeLoopCliCommand(
      `wakeloop agents repair --agent ${error.agentId}`
    )}\`, then return to WakeLoop Web setup. Detail: ${error.reason}`;
  }

  const nextStep = error.nextSteps?.[0] ?? null;
  if (nextStep) {
    return `WakeLoop could not connect ${error.agentId} on this computer. Run \`${formatWakeLoopCliCommand(
      nextStep
    )}\`, then return to WakeLoop Web setup. Detail: ${error.reason}`;
  }

  return `WakeLoop could not connect ${error.agentId} on this computer. Detail: ${error.reason}`;
}

function formatSelectedLocalAgentRepairFailure(
  error: StartLocalAgentsRepairError
): string {
  if (error.code === "controller.launch.choice_required") {
    return formatSelectedLocalAgentInstallChoiceFailure({
      command: "repair",
      error,
    });
  }

  return `WakeLoop could not repair ${error.agentId} on this computer. Run \`${formatWakeLoopCliCommand(
    `wakeloop agents repair --agent ${error.agentId}`
  )}\`, then return to WakeLoop Web setup. Detail: ${error.reason}`;
}

function formatSelectedLocalAgentInstallChoiceFailure(input: {
  command: "enable" | "repair";
  error: SelectedLocalAgentInstallChoiceError;
}): string {
  const commands =
    input.error.nextSteps && input.error.nextSteps.length > 0
      ? input.error.nextSteps
      : (input.error.installChoices ?? []).map(
          (installChoice) =>
            `wakeloop agents ${input.command} --agent ${input.error.agentId} --install ${installChoice.installId}`
        );
  const reason =
    normalizeOptionalId(input.error.reason) ||
    `WakeLoop found more than one ${input.error.agentId} install on this computer.`;
  if (commands.length > 0) {
    return `${reason} Run one of these commands, then return to WakeLoop Web setup: ${formatCommandChoiceList(commands)}.`;
  }
  return `${reason} Run \`${formatWakeLoopCliCommand(
    `wakeloop agents ${input.command} --agent ${input.error.agentId}`
  )}\` in an interactive Terminal and choose one install, then return to WakeLoop Web setup.`;
}

function formatLocalAgentDisplayName(agentId: string): string {
  return agentId
    .split("-")
    .filter((part) => part.length > 0)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

function resolveSelectedLocalAgentEnableNextSteps(
  error: StartLocalAgentsEnableError
): string[] {
  if (error.nextSteps && error.nextSteps.length > 0) {
    return [...error.nextSteps];
  }

  return (error.installChoices ?? []).map(
    (installChoice) =>
      `wakeloop agents enable --agent ${error.agentId} --install ${installChoice.installId}`
  );
}

function formatCommandChoiceList(commands: readonly string[]): string {
  return commands
    .map((command) => `\`${formatWakeLoopCliCommand(command)}\``)
    .join(" or ");
}

type PrepareSessionTerminalAuthCheck =
  | {
      status: "ok";
    }
  | {
      message: string;
      reasonCode: string;
      status: "blocked";
    };

export function resolvePrepareSessionTerminalAuthCheck(input: {
  authState: AuthSessionStateResult;
  snapshot: Pick<PrepareSessionSnapshot, "ownerUserId">;
}): PrepareSessionTerminalAuthCheck {
  if (input.authState.state !== "valid" || input.authState.session === null) {
    return {
      status: "blocked",
      reasonCode: "prepare.auth.sign_in_required",
      message: `This Terminal is not signed in to WakeLoop. Run \`${formatWakeLoopCliCommand(
        "wakeloop login"
      )}\` with the same account shown in WakeLoop Web, then copy a fresh setup command from WakeLoop Web and run setup again.`,
    };
  }

  const terminalUserId = normalizeOptionalId(input.authState.session.user?.id);
  if (!terminalUserId) {
    return {
      status: "blocked",
      reasonCode: "prepare.auth.account_unknown",
      message: `This Terminal sign-in is missing its WakeLoop account ID. Run \`${formatWakeLoopCliCommand(
        "wakeloop login"
      )}\` again with the same account shown in WakeLoop Web, then copy a fresh setup command from WakeLoop Web and run setup again.`,
    };
  }

  if (terminalUserId !== input.snapshot.ownerUserId) {
    const terminalAccount = normalizeOptionalId(
      input.authState.session.user?.email
    );
    return {
      status: "blocked",
      reasonCode: "prepare.auth.account_mismatch",
      message: `This Terminal is signed in${terminalAccount ? ` as ${terminalAccount}` : ""}, but WakeLoop Web is using a different account. Run \`${formatWakeLoopCliCommand(
        "wakeloop login"
      )}\` with the account shown in WakeLoop Web, then copy a fresh setup command from WakeLoop Web and run setup again.`,
    };
  }

  return { status: "ok" };
}

async function enforcePrepareSessionTerminalAuth(input: {
  input: PrepareSessionExecutorInput;
  stream: PrepareSessionStream;
  snapshot: PrepareSessionSnapshot;
}): Promise<void> {
  const result = resolvePrepareSessionTerminalAuthCheck({
    authState: await getAuthSessionState(),
    snapshot: input.snapshot,
  });
  if (result.status === "ok") {
    return;
  }
  const connectionEpoch = requireConnectionEpoch(input.snapshot);
  const beforeWriteRevision =
    input.stream.getLatestSnapshot()?.sessionRevision ??
    input.snapshot.sessionRevision;
  input.stream.writeTerminalStep({
    connectionEpoch,
    expectedRevision: input.snapshot.sessionRevision,
    stepId: "connect_terminal",
    status: "failed",
    reasonCode: result.reasonCode,
    message: result.message,
  });
  await input.stream.waitForSnapshot((snapshot) => {
    const step = findStep(snapshot, "connect_terminal");
    return (
      snapshot.sessionRevision > beforeWriteRevision &&
      step?.state === "blocked"
    );
  });
  emitHumanStatus(input.input, "Setup blocked", [
    result.message,
    `Support code: ${result.reasonCode}`,
  ]);
  throw new Error(result.message);
}

async function connectSelectedLocalAgentsToWakeLoop(input: {
  wakeLoopServiceRuntimeIdentity?: TerminalRuntimeIdentity;
  gatewayUrl: string;
  localAgentIds: readonly string[];
  syncRouteCatalog?: boolean;
}): Promise<TerminalStepRunResult> {
  let lastTransientError: unknown = null;
  for (
    let attempt = 1;
    attempt <= REPORT_RUNTIME_CONNECTION_MAX_ATTEMPTS;
    attempt++
  ) {
    try {
      return await connectSelectedLocalAgentsToWakeLoopOnce(input);
    } catch (error) {
      if (
        !isTransientSelectedAgentConnectionError(error) ||
        attempt >= REPORT_RUNTIME_CONNECTION_MAX_ATTEMPTS
      ) {
        throw error;
      }
      lastTransientError = error;
      await sleep(REPORT_RUNTIME_CONNECTION_RETRY_DELAY_MS);
    }
  }

  throw (
    lastTransientError ?? new Error("WakeLoop could not connect local agents.")
  );
}

async function connectSelectedLocalAgentsToWakeLoopOnce(input: {
  wakeLoopServiceRuntimeIdentity?: TerminalRuntimeIdentity;
  gatewayUrl: string;
  localAgentIds: readonly string[];
  syncRouteCatalog?: boolean;
}): Promise<TerminalStepRunResult> {
  const wakeLoopServiceRuntimeIdentity =
    input.wakeLoopServiceRuntimeIdentity ??
    (await syncLocalSetupCurrentDeviceTargetFromServiceContract({
      gatewayUrl: input.gatewayUrl,
    }));
  const readiness = await collectStartLocalReadiness({
    auth: {
      status: "valid",
      email: null,
      authBaseUrl: null,
      gatewayUrl: input.gatewayUrl,
    },
  });
  const runtimeReports = await submitSelectedAgentControllerReports({
    readiness,
    localAgentIds: input.localAgentIds,
  });
  assertSelectedAgentControllerReportsAccepted(runtimeReports);
  if (input.syncRouteCatalog) {
    await notifyRouteCatalogAfterPrepare({
      reason: "prepare_selected_agents_reported",
    });
  }
  return {
    ...wakeLoopServiceRuntimeIdentity,
    localReadinessSnapshot: buildPrepareLocalReadinessSnapshot(readiness),
    runtimeReports,
  };
}

function buildPrepareSessionExecutorCompletionFacts(input: {
  reportRuntimeResult: TerminalStepRunResult;
  selectedLocalAgentIds: readonly string[];
}): PrepareSessionExecutorCompletionFacts {
  return {
    wakeLoopServiceRuntimeIdentity: {
      deviceId: input.reportRuntimeResult.deviceId,
      runtimeLane: input.reportRuntimeResult.runtimeLane,
    },
    runtimeReports: input.reportRuntimeResult.runtimeReports ?? [],
    selectedLocalAgentIds: [...input.selectedLocalAgentIds],
  };
}

function isTransientSelectedAgentConnectionError(error: unknown): boolean {
  const rawMessage = toErrorMessage(error).toLowerCase();
  return (
    rawMessage.includes("service has not finished starting") ||
    rawMessage.includes("wakeloop service is not running") ||
    rawMessage.includes("service.not_running") ||
    rawMessage.includes("service_contract_unavailable") ||
    rawMessage.includes("runtime.reporting.write_failed")
  );
}

async function verifySelectedLocalAgentsConnectedToWakeLoop(input: {
  gatewayUrl: string;
  localAgentIds: readonly string[];
}): Promise<TerminalStepRunResult> {
  const wakeLoopServiceRuntimeIdentity =
    await readTerminalRuntimeIdentityFromServiceContract();
  const readiness = await collectStartLocalReadiness({
    auth: {
      status: "valid",
      email: null,
      authBaseUrl: null,
      gatewayUrl: input.gatewayUrl,
    },
  });
  const selectedReadiness = resolveSelectedAgentControllerReadiness({
    readiness,
    localAgentIds: input.localAgentIds,
  });
  assertSelectedAgentControllerReadinessReady(selectedReadiness);
  await assertSelectedRuntimeReadinessIsReadableByWakeLoop({
    gatewayUrl: input.gatewayUrl,
    wakeLoopServiceRuntimeIdentity,
    selectedReadiness,
  });
  return {
    ...wakeLoopServiceRuntimeIdentity,
    localReadinessSnapshot: buildPrepareLocalReadinessSnapshot(readiness),
  };
}

async function readTerminalRuntimeIdentityFromServiceContract(): Promise<TerminalRuntimeIdentity> {
  // WAKELOOP_SETUP_TO_WAKE_PATH: HIGHEST_FACT service-session
  // The service contract provides this computer's account/lane/device identity.
  // Other local setup evidence must not replace this fact.
  const serviceContract = await readDaemonServiceContract();
  const deviceId = serviceContract?.deviceId;
  const runtimeLane = serviceContract?.lane;
  if (!(deviceId && runtimeLane)) {
    throw new Error(
      `WakeLoop could not verify selected agents because WakeLoop Service did not provide this computer's device identity yet. Keep WakeLoop Service running, then run \`${formatWakeLoopCliCommand(
        "wakeloop setup --view agent"
      )}\` again. You can also run \`${formatWakeLoopCliCommand(
        "wakeloop service status --view agent"
      )}\` to inspect the local service.`
    );
  }
  return {
    deviceId,
    runtimeLane,
  };
}

async function notifyRouteCatalogAfterPrepare(input: {
  reason: string;
}): Promise<void> {
  await notifyDaemonRouteCatalogChanged({
    reason: input.reason,
  });
}

async function assertSelectedRuntimeReadinessIsReadableByWakeLoop(input: {
  gatewayUrl: string;
  wakeLoopServiceRuntimeIdentity: TerminalRuntimeIdentity;
  selectedReadiness: readonly SelectedAgentControllerReadinessDecision[];
}): Promise<void> {
  // WAKELOOP_SETUP_TO_WAKE_PATH: HIGHEST_FACT agent-controller-report
  // Prepare verifies that selected local capabilities are readable by WakeLoop. It
  // does not prove a later Space Wake will run successfully.
  const agentControllerRefs = input.selectedReadiness.map(
    (decision) => decision.agentControllerRef
  );
  if (agentControllerRefs.length === 0) {
    return;
  }

  const response = await createPrepareReadinessApi(
    createCliApiClientFoundation(input.gatewayUrl)
  ).getReadiness({
    agentControllerRefs,
    deviceId: input.wakeLoopServiceRuntimeIdentity.deviceId,
    lane: input.wakeLoopServiceRuntimeIdentity.runtimeLane,
  });
  const runnableAgentControllerRefs = new Set(
    response.runnableCapabilities.map(
      (capability) => capability.agentControllerRef
    )
  );
  const missingAgentControllerRef = agentControllerRefs.find(
    (agentControllerRef) => !runnableAgentControllerRefs.has(agentControllerRef)
  );

  if (
    response.status === "ready" &&
    response.wakeLoopServiceRuntimeId &&
    !missingAgentControllerRef
  ) {
    return;
  }

  const reasonCode =
    response.summary.reasonCodes[0] ??
    response.reason ??
    (missingAgentControllerRef
      ? "prepare_readiness.capability_not_ready"
      : "prepare_readiness.not_ready");
  throw new Error(
    `WakeLoop could not verify that selected local agents were reported to WakeLoop yet. Keep WakeLoop Service running, then run \`${formatWakeLoopCliCommand(
      "wakeloop setup --view agent"
    )}\` again. You can also run \`${formatWakeLoopCliCommand(
      "wakeloop service status --view agent"
    )}\` to inspect the local service. Support code: ${reasonCode}.`
  );
}

function buildPrepareLocalReadinessSnapshot(
  readiness: StartReadinessSnapshot
): PrepareLocalReadinessSnapshot {
  return {
    observedAt: new Date().toISOString(),
    service: {
      installStatus: readiness.service.status,
      runtimeStatus: readiness.service.runtime?.status ?? "unknown",
      installedVersion: readiness.service.installedVersion,
      expectedVersion: readiness.service.expectedVersion,
      ...(readiness.service.executionCompatibility
        ? { executionCompatibility: readiness.service.executionCompatibility }
        : {}),
    },
    skills: {
      status: readiness.skills.status,
      targetCount: readiness.skills.targetCount,
      summary: { ...readiness.skills.summary },
    },
  };
}

// Best-effort launchd job-state verdict for a failed refresh_service step so the
// setup failure surfaced on WakeLoop Web names the root cause (never-spawned / crash
// loop) instead of the generic copy. Diagnostic evidence only; a null verdict
// falls back to the generic message.
async function resolveRefreshServiceJobVerdict(input: {
  stepId: PrepareStepId;
  resolveServiceJobVerdict?: typeof resolvePrepareServiceJobVerdict;
}): Promise<PrepareServiceJobVerdict | null> {
  if (input.stepId !== "refresh_service") {
    return null;
  }
  const resolve =
    input.resolveServiceJobVerdict ?? resolvePrepareServiceJobVerdict;
  return await resolve().catch(() => null);
}

export function formatTerminalStepFailureMessage(input: {
  rawMessage: string;
  stepId: PrepareStepId;
  serviceJobVerdict?: PrepareServiceJobVerdict | null;
}): string {
  if (input.rawMessage.includes("prepare session revision conflict")) {
    return "This setup changed while Terminal was working. Copy a fresh setup command from WakeLoop Web and run setup again to continue from the latest step.";
  }
  if (input.stepId === "refresh_service") {
    if (input.serviceJobVerdict === "never_spawned") {
      return `macOS is blocking the WakeLoop Service background item, so WakeLoop Service never launched. Path A: open System Settings > General > Login Items & Extensions, enable the WakeLoop background item, then copy a fresh setup command from WakeLoop Web and run setup again. Path B: run \`${formatWakeLoopCliCommand(
        "wakeloop service start --mode foreground"
      )}\` in a separate terminal and keep it open (Wake stops when you close that window), then run setup again.`;
    }
    if (input.serviceJobVerdict === "crashed") {
      return `WakeLoop Service started but keeps exiting on this Mac. Run \`${formatWakeLoopCliCommand(
        "wakeloop service start --mode foreground"
      )}\` to see the live error, fix it, then copy a fresh setup command from WakeLoop Web and run setup again.`;
    }
    const detail = formatTerminalStepFailureDetail(input.rawMessage);
    return `WakeLoop Service did not report a running local connection during setup. Copy a fresh setup command from WakeLoop Web and run setup again to continue from this step. If it fails again, run \`${formatWakeLoopCliCommand(
      "wakeloop service start --mode foreground"
    )}\` to see live startup logs, then copy a fresh setup command from WakeLoop Web and run setup again.${
      detail ? ` Detail: ${detail}` : ""
    }`;
  }
  return input.rawMessage;
}

function formatTerminalStepFailureDetail(rawMessage: string): string | null {
  const normalized = rawMessage.replace(/\s+/g, " ").trim();
  if (
    normalized.length === 0 ||
    normalized === "WakeLoop Service is not connected yet."
  ) {
    return null;
  }
  const maxLength = 220;
  return normalized.length > maxLength
    ? `${normalized.slice(0, maxLength - 3)}...`
    : normalized;
}

function resolveTerminalStepFailureReasonCode(input: {
  rawMessage: string;
  stepId: PrepareStepId;
  serviceJobVerdict?: PrepareServiceJobVerdict | null;
}): string {
  if (input.rawMessage.includes("runtime.registration.device_forgotten")) {
    return "runtime.registration.device_forgotten";
  }
  if (input.stepId === "refresh_service") {
    if (input.serviceJobVerdict === "never_spawned") {
      return "prepare.refresh_service.service_never_spawned";
    }
    if (input.serviceJobVerdict === "crashed") {
      return "prepare.refresh_service.service_crash_loop";
    }
  }
  return `prepare.${input.stepId}.failed`;
}

function findStep(snapshot: PrepareSessionSnapshot, stepId: PrepareStepId) {
  return snapshot.steps.find((step) => step.id === stepId) ?? null;
}

type TerminalStepWriteInput = {
  connectionEpoch: number;
  deviceId?: string;
  expectedRevision: number;
  localReadinessSnapshot?: PrepareLocalReadinessSnapshot;
  runtimeLane?: string;
  stepId: PrepareStepId;
  status: "completed" | "failed" | "started";
  reasonCode?: string;
  message?: string;
  diagnostics?: string;
};

type TerminalRuntimeIdentity = {
  deviceId: string;
  runtimeLane: string;
};

type TerminalStepRunResult = TerminalRuntimeIdentity & {
  localReadinessSnapshot?: PrepareLocalReadinessSnapshot;
  runtimeReports?: readonly SelectedAgentControllerReportResult[];
};

function resolveRuntimeIdentityFromPrepareSessionSnapshot(
  snapshot: PrepareSessionSnapshot
): TerminalRuntimeIdentity | null {
  if (!(snapshot.deviceId && snapshot.runtimeLane)) {
    return null;
  }
  return {
    deviceId: snapshot.deviceId,
    runtimeLane: snapshot.runtimeLane,
  };
}

type TerminalLocalAgentCatalogWriteInput = {
  connectionEpoch: number;
  localAgentCatalog: PrepareLocalAgentCatalog;
};

export type PrepareSessionStream = {
  close(): void;
  getLatestSnapshot(): PrepareSessionSnapshot | null;
  waitForSnapshot(
    predicate: (snapshot: PrepareSessionSnapshot) => boolean
  ): Promise<PrepareSessionSnapshot>;
  writeTerminalLocalAgentCatalog(
    input: TerminalLocalAgentCatalogWriteInput
  ): void;
  writeTerminalAgentSelection(input: {
    connectionEpoch: number;
    expectedRevision: number;
    localAgentIds: string[];
  }): void;
  writeTerminalStep(input: TerminalStepWriteInput): void;
};

export function buildPrepareSessionClientHelloFrame(input: {
  cliVersion: string;
}) {
  return {
    type: "client.hello" as const,
    payload: {
      cliVersion: input.cliVersion,
      protocolVersion: PREPARE_SESSION_STREAM_PROTOCOL_VERSION,
    },
  };
}

export function isLegacyPrepareSessionClientHelloRejection(input: {
  code: string;
  message: string;
}): boolean {
  return (
    input.code === "schema.invalid" &&
    input.message === "Invalid prepare session frame."
  );
}

export function isPrepareSessionFrameSchemaRejection(input: {
  code: string;
}): boolean {
  // Cores that predate a client frame field fail to parse the whole frame:
  // current cores answer prepare.session.protocol_mismatch, the oldest ones
  // schema.invalid. Neither closes the socket, so a stripped resend can land.
  return (
    input.code === "prepare.session.protocol_mismatch" ||
    input.code === "schema.invalid"
  );
}

const TERMINAL_STEP_FAILURE_SETTLE_TIMEOUT_MS = 2000;

async function waitForTerminalStepWriteToSettle(input: {
  revisionFloor: number;
  stream: PrepareSessionStream;
}): Promise<void> {
  let timer: NodeJS.Timeout | null = null;
  try {
    await Promise.race([
      input.stream.waitForSnapshot(
        (snapshot) => snapshot.sessionRevision > input.revisionFloor
      ),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, TERMINAL_STEP_FAILURE_SETTLE_TIMEOUT_MS);
      }),
    ]);
  } catch {
    // Best-effort: a broken stream must not mask the original step failure.
  } finally {
    if (timer !== null) {
      clearTimeout(timer);
    }
  }
}

export async function waitForSnapshotWithProgress(input: {
  input: PrepareSessionExecutorInput;
  predicate: (snapshot: PrepareSessionSnapshot) => boolean;
  progress?: SetupProgressStepHandle;
  stream: PrepareSessionStream;
  waitingMessage: string;
}): Promise<PrepareSessionSnapshot> {
  let progress: SetupProgressStepHandle | null = input.progress ?? null;
  let progressStartedByWait = false;
  const waitingProgressTimer = setTimeout(() => {
    if (input.progress) {
      input.progress.detail({
        code: "prepare.session.waiting",
        text: input.waitingMessage,
      });
      return;
    }
    progress = startPrepareProgressStep(input.input, {
      code: "prepare.session.waiting",
      text: input.waitingMessage,
    });
    progressStartedByWait = true;
  }, WAITING_PROGRESS_DELAY_MS);
  try {
    return await input.stream.waitForSnapshot(input.predicate);
  } finally {
    clearTimeout(waitingProgressTimer);
    if (progressStartedByWait) {
      progress?.cancel();
    }
  }
}

async function connectPrepareSessionStream(input: {
  gatewayUrl: string;
  opaqueToken: string;
  prepareSessionId: string;
}): Promise<PrepareSessionStream> {
  const ws = await connectWebSocket(
    buildPrepareSessionStreamUrl(input.gatewayUrl, input.prepareSessionId),
    {
      headers: {
        [PREPARE_TOKEN_HEADER]: input.opaqueToken,
      },
    }
  );
  let latestSnapshot: PrepareSessionSnapshot | null = null;
  const waiters: Array<{
    reject: (error: Error) => void;
    predicate: (snapshot: PrepareSessionSnapshot) => boolean;
    resolve: (snapshot: PrepareSessionSnapshot) => void;
  }> = [];
  const rejectWaiters = (error: Error) => {
    for (const waiter of waiters.splice(0)) {
      waiter.reject(error);
    }
  };
  let closedError: PrepareSessionConnectionInterruptedError | null = null;
  let stateWriteSent = false;
  // Stripped copy of the last diagnostics-carrying step write. Cores that
  // predate the diagnostics field reject the whole frame, so the base failure
  // report is resent once without it; snapshots clear the pending copy.
  let pendingStepWriteFallbackFrame: unknown = null;
  const assertStreamOpen = (): void => {
    if (closedError) {
      throw closedError;
    }
  };
  const sendFrame = (frame: unknown): void => {
    assertStreamOpen();
    try {
      ws.send(JSON.stringify(frame));
    } catch (error) {
      closedError = new PrepareSessionConnectionInterruptedError(
        null,
        error instanceof Error ? error.message : "connection failed"
      );
      throw closedError;
    }
  };

  const handleServerErrorFrame = (payload: {
    code: string;
    message: string;
  }): void => {
    if (
      !stateWriteSent &&
      isLegacyPrepareSessionClientHelloRejection(payload)
    ) {
      return;
    }
    if (
      pendingStepWriteFallbackFrame !== null &&
      isPrepareSessionFrameSchemaRejection(payload)
    ) {
      const fallbackFrame = pendingStepWriteFallbackFrame;
      pendingStepWriteFallbackFrame = null;
      try {
        sendFrame(fallbackFrame);
      } catch {
        // Connection lost; the diagnostics attach stays best-effort.
      }
      return;
    }
    rejectWaiters(errorFromPrepareSessionServerError(payload));
  };

  ws.onMessage((raw) => {
    const frame = parseServerFrame(raw);
    if (!frame) {
      return;
    }
    if (frame.type === "terminal.replaced") {
      rejectWaiters(
        new PrepareSessionTerminalReplacedError(frame.payload.message)
      );
      ws.close(4000, "terminal_replaced");
      return;
    }
    if (frame.type === "server.error") {
      handleServerErrorFrame(frame.payload);
      return;
    }
    if (frame.type !== "snapshot" && frame.type !== "snapshot_changed") {
      return;
    }

    pendingStepWriteFallbackFrame = null;
    const snapshot: PrepareSessionSnapshot = frame.payload.snapshot;
    latestSnapshot = snapshot;
    for (const waiter of [...waiters]) {
      if (!waiter.predicate(snapshot)) {
        continue;
      }
      waiters.splice(waiters.indexOf(waiter), 1);
      waiter.resolve(snapshot);
    }
  });
  ws.onClose((code, reason) => {
    closedError = new PrepareSessionConnectionInterruptedError(code, reason);
    rejectWaiters(closedError);
  });
  ws.onError((error) => {
    closedError = new PrepareSessionConnectionInterruptedError(
      null,
      error instanceof Error ? error.message : "connection failed"
    );
    rejectWaiters(closedError);
  });
  sendFrame(
    buildPrepareSessionClientHelloFrame({
      cliVersion: getCliVersionInfo().version,
    })
  );

  return {
    close: () => ws.close(1000, "prepare_complete"),
    getLatestSnapshot: () => latestSnapshot,
    waitForSnapshot: async (predicate) => {
      const snapshot = latestSnapshot;
      if (snapshot && predicate(snapshot)) {
        return snapshot;
      }
      assertStreamOpen();
      return await new Promise<PrepareSessionSnapshot>((resolve, reject) => {
        waiters.push({ predicate, reject, resolve });
      });
    },
    writeTerminalStep: (step) => {
      if (!latestSnapshot) {
        throw new Error("Setup session snapshot is not available yet.");
      }
      stateWriteSent = true;
      const basePayload = {
        prepareSessionId: latestSnapshot.prepareSessionId,
        connectionEpoch: step.connectionEpoch,
        expectedRevision: step.expectedRevision,
        ...(step.deviceId === undefined ? {} : { deviceId: step.deviceId }),
        ...(step.runtimeLane === undefined
          ? {}
          : { runtimeLane: step.runtimeLane }),
        stepAttemptId: crypto.randomUUID(),
        stepId: step.stepId,
        status: step.status,
        ...(step.reasonCode === undefined
          ? {}
          : { reasonCode: step.reasonCode }),
        ...(step.localReadinessSnapshot === undefined
          ? {}
          : { localReadinessSnapshot: step.localReadinessSnapshot }),
        ...(step.message === undefined ? {} : { message: step.message }),
      };
      if (step.diagnostics === undefined) {
        pendingStepWriteFallbackFrame = null;
        sendFrame({ type: "terminal.step.write", payload: basePayload });
        return;
      }
      pendingStepWriteFallbackFrame = {
        type: "terminal.step.write",
        payload: basePayload,
      };
      sendFrame({
        type: "terminal.step.write",
        payload: { ...basePayload, diagnostics: step.diagnostics },
      });
    },
    writeTerminalLocalAgentCatalog: (catalog) => {
      if (!latestSnapshot) {
        throw new Error("Setup session snapshot is not available yet.");
      }
      stateWriteSent = true;
      sendFrame({
        type: "terminal.local_agent_catalog.write",
        payload: {
          prepareSessionId: latestSnapshot.prepareSessionId,
          connectionEpoch: catalog.connectionEpoch,
          localAgentCatalog: catalog.localAgentCatalog,
        },
      });
    },
    writeTerminalAgentSelection: (selection) => {
      if (!latestSnapshot) {
        throw new Error("Setup session snapshot is not available yet.");
      }
      stateWriteSent = true;
      sendFrame({
        type: "terminal.agent_selection.write",
        payload: {
          prepareSessionId: latestSnapshot.prepareSessionId,
          connectionEpoch: selection.connectionEpoch,
          expectedRevision: selection.expectedRevision,
          localAgentIds: selection.localAgentIds,
        },
      });
    },
  };
}

async function resolvePrepareSessionToken(input: {
  gatewayUrl: string;
  opaqueToken: string;
}): Promise<PrepareSessionSnapshot> {
  const response = await fetchSpaceRequestWithTimeout({
    url: new URL(
      "/v1/prepare/sessions/resolve-token",
      input.gatewayUrl
    ).toString(),
    operation: "prepare.session.resolve_token",
    init: {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        opaqueToken: input.opaqueToken,
      }),
    },
  });
  if (!response.ok) {
    throw new Error(await response.text());
  }
  const parsed = prepareSessionResponseSchema.safeParse(await response.json());
  if (!parsed.success) {
    throw new Error("Invalid prepare session response.");
  }
  return parsed.data.snapshot;
}

function buildPrepareSessionStreamUrl(
  gatewayUrl: string,
  prepareSessionId: string
): string {
  const url = new URL(
    `/v1/prepare/sessions/${encodeURIComponent(prepareSessionId)}/stream`,
    gatewayUrl
  );
  url.searchParams.set("role", "terminal");
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

function parseServerFrame(raw: string): PrepareSessionStreamServerFrame | null {
  let json: unknown;
  try {
    json = JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
  const parsed = prepareSessionStreamServerFrameSchema.safeParse(json);
  return parsed.success ? parsed.data : null;
}

function requireConnectionEpoch(snapshot: PrepareSessionSnapshot): number {
  const epoch = snapshot.terminalConnection.connectionEpoch;
  if (!epoch) {
    throw new Error("WakeLoop Web has not connected this Terminal yet.");
  }
  return epoch;
}

function normalizeOptionalId(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function emitHumanLine(input: PrepareSessionExecutorInput, text: string): void {
  if (input.runtime.resolvedView !== "human") {
    return;
  }
  input.presenter.line({
    code: "prepare.session.status",
    text,
  });
}

function emitHumanSection(
  input: PrepareSessionExecutorInput,
  title: string
): void {
  if (input.runtime.resolvedView !== "human") {
    return;
  }
  emitHumanLine(input, "");
  input.presenter.line({
    code: "prepare.session.section",
    text: title,
  });
}

function emitPrepareProgressLine(
  input: PrepareSessionExecutorInput,
  progress: {
    code?: string;
    data?: Record<string, unknown>;
    state: "pending" | "success";
    text: string;
  }
): void {
  createPrepareProgressReporter(input).line({
    code: progress.code ?? `prepare.session.progress.${progress.state}`,
    data: progress.data,
    state: progress.state,
    text: progress.text,
  });
}

function emitPrepareProgressDetail(
  input: PrepareSessionExecutorInput,
  text: string,
  code?: string
): void {
  createPrepareProgressReporter(input).detail({
    code: code ?? "prepare.session.progress.detail",
    text,
  });
}

function startPrepareProgressStep(
  input: PrepareSessionExecutorInput,
  progress: {
    code: string;
    data?: Record<string, unknown>;
    text: string;
  }
): SetupProgressStepHandle {
  return createPrepareProgressReporter(input).startStep({
    code: progress.code,
    data: progress.data,
    text: progress.text,
  });
}

function createPrepareProgressReporter(
  input: PrepareSessionExecutorInput
): ReturnType<typeof createSetupProgressReporter> {
  return createSetupProgressReporter({
    presenter: input.presenter,
    runtime: input.runtime,
  });
}

function emitHumanStatus(
  input: PrepareSessionExecutorInput,
  title: string,
  lines: readonly string[]
): void {
  if (input.runtime.resolvedView !== "human") {
    return;
  }
  emitHumanLine(input, "");
  renderInfoCard({
    presenter: input.presenter,
    title,
    codePrefix: `prepare.session.${toStatusCodeSlug(title)}`,
    rows: lines.map((line) => ({ label: "", value: line })),
    sanitize: true,
    minContentWidth: 56,
  });
}

function formatSelectedLocalAgentNames(input: {
  localAgentIds: readonly string[];
  snapshot: PrepareSessionSnapshot;
}): string {
  const entriesById = new Map(
    (input.snapshot.localAgentCatalog?.entries ?? []).map((entry) => [
      entry.agentId,
      entry,
    ])
  );
  return input.localAgentIds
    .map((agentId) => entriesById.get(agentId)?.displayName ?? agentId)
    .join(", ");
}

function toStatusCodeSlug(title: string): string {
  const slug = title
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "_")
    .replaceAll(/^_+|_+$/g, "");
  return slug.length > 0 ? slug : "status";
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message.trim();
    return message.length > 0 ? message : "unknown error";
  }
  const message = String(error ?? "").trim();
  return message.length > 0 ? message : "unknown error";
}

class PrepareSessionTerminalReplacedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PrepareSessionTerminalReplacedError";
  }
}

class PrepareSessionRevisionConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PrepareSessionRevisionConflictError";
  }
}

export class PrepareSessionConnectionInterruptedError extends Error {
  constructor(code: number | null, reason: string) {
    const suffix =
      code === null ? reason : `${code}${reason ? `: ${reason}` : ""}`;
    super(`Prepare session connection interrupted (${suffix}).`);
    this.name = "PrepareSessionConnectionInterruptedError";
  }
}

function errorFromPrepareSessionServerError(input: {
  code: string;
  message: string;
}): Error {
  if (input.code === "prepare.session.terminal_stale") {
    return new PrepareSessionTerminalReplacedError(input.message);
  }
  if (input.code === "prepare.session.revision_conflict") {
    return new PrepareSessionRevisionConflictError(input.message);
  }
  return new Error(input.message);
}
